import { memo, type CSSProperties } from "react";
import logoBranca from "@/assets/logo-branca-web-160.webp";
import { cn } from "@/lib/utils";

const container = "mx-auto w-full max-w-7xl px-5 sm:px-8";

function Bar({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <div
      className={cn("rounded-full bg-white/10 motion-safe:animate-pulse", className)}
      style={style}
    />
  );
}

/**
 * Skeleton de boot da landing comercial (raiz sem org): mesmo fundo escuro,
 * nav e hero da `SysIgrejaLanding`, para não piscar o layout de site de igreja.
 * Sem framer-motion para não pesar o chunk de fallback.
 */
export const LandingBootSkeleton = memo(function LandingBootSkeleton() {
  return (
    <div
      className="min-h-screen bg-[#070a1f] font-sans antialiased"
      aria-busy="true"
      aria-label="Carregando"
    >
      <header className="fixed inset-x-0 top-0 z-50 border-b border-transparent">
        <nav className={cn(container, "flex h-16 items-center justify-between gap-6")}>
          <img src={logoBranca} alt="SysIgreja" className="h-8 w-auto" width={61} height={32} />
          <div className="hidden items-center gap-2 md:flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <Bar key={i} className="h-4 w-16" />
            ))}
          </div>
          <Bar className="h-9 w-9 md:h-10 md:w-44" />
        </nav>
      </header>

      <section className="relative isolate overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-40 left-1/2 h-[720px] w-[1200px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(99,102,241,0.45),transparent)]" />
          <div className="absolute top-60 -right-40 h-[520px] w-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(168,85,247,0.35),transparent)]" />
        </div>

        <div className={cn(container, "flex flex-col items-center text-center")}>
          <Bar className="h-8 w-72 max-w-full" />
          <Bar className="mt-8 h-10 w-full max-w-3xl rounded-2xl sm:h-14 md:h-16" />
          <Bar className="mt-3 h-10 w-2/3 max-w-xl rounded-2xl sm:h-14 md:h-16" />
          <Bar className="mt-8 h-5 w-full max-w-2xl" />
          <Bar className="mt-3 h-5 w-4/5 max-w-xl" />
          <div className="mt-6 flex max-w-2xl flex-wrap justify-center gap-2">
            {[16, 20, 16, 14, 28, 20].map((w, i) => (
              <Bar key={i} className="h-7" style={{ width: `${w * 4}px` }} />
            ))}
          </div>
          <div className="mt-10 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row">
            <Bar className="h-12 sm:w-60" />
            <Bar className="h-12 sm:w-44" />
          </div>
          <Bar className="mt-5 h-4 w-80 max-w-full" />
        </div>

        <div className={cn(container, "relative mt-16 md:mt-20")}>
          <div className="mx-auto max-w-[340px] rounded-[1.75rem] bg-white/5 p-1.5 ring-1 ring-white/15 sm:max-w-6xl md:p-2">
            <div className="aspect-[9/16] rounded-[1.35rem] bg-slate-900 motion-safe:animate-pulse sm:aspect-video" />
          </div>
        </div>
      </section>
    </div>
  );
});
